import React from 'react';

interface ClockHandsProps {
  selectedHour: number;
}

const ClockHands: React.FC<ClockHandsProps> = ({ selectedHour }) => {
  // Convert 12-hour to angle (12 = 0°, 3 = 90°, etc.)
  const hourAngle = ((selectedHour % 12) / 12) * 360;
  
  // Minute hand at 12 (0 degrees)
  const minuteAngle = 0;
  
  // Second hand animation
  const [secondAngle, setSecondAngle] = React.useState(0);
  
  React.useEffect(() => {
    const interval = setInterval(() => {
      setSecondAngle(prev => (prev + 6) % 360);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Get CSS variable values
  const getColorVariable = (variableName: string) => {
    return getComputedStyle(document.documentElement)
      .getPropertyValue(variableName)
      .trim();
  };

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: 'none',
      }}
    >
      {/* Hour hand - Using primary color */}
      <div
        style={{
          position: 'absolute',
          width: '6px',
          height: '28%',
          background: `linear-gradient(to top, 
            var(--color-primary, #dc2626), 
            var(--color-primary-light, #ef4444)
          )`,
          borderRadius: '3px',
          transformOrigin: 'bottom center',
          transform: `translateY(-50%) rotate(${hourAngle}deg)`,
          boxShadow: '0 0 10px rgba(var(--color-primary-rgb, 220, 38, 38), 0.5)',
          transition: 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
          zIndex: 15,
        }}
      />
      
      {/* Minute hand - Using accent/secondary color */}
      <div
        style={{
          position: 'absolute',
          width: '4px',
          height: '35%',
          background: `linear-gradient(to top, 
            var(--color-accent, #8b5cf6) 0%, 
            var(--color-secondary, #3b82f6) 100%
          )`,
          borderRadius: '2px',
          transformOrigin: 'bottom center',
          transform: `translateY(-50%) rotate(${minuteAngle}deg)`,
          boxShadow: '0 0 8px rgba(var(--color-accent-rgb, 139, 92, 246), 0.5)',
          zIndex: 14,
        }}
      />
      
      {/* Second hand - Using warning/alert color */}
      <div
        style={{
          position: 'absolute',
          width: '2px',
          height: '40%',
          background: 'var(--color-warning, #f97316)', // Orange as fallback
          borderRadius: '1px',
          transformOrigin: 'bottom center',
          transform: `translateY(-50%) rotate(${secondAngle}deg)`,
          boxShadow: '0 0 6px rgba(var(--color-warning-rgb, 249, 115, 22), 0.5)',
          transition: 'transform 0.1s linear',
          zIndex: 16,
        }}
      />
      
      {/* Center cap - Using gradient from primary to accent */}
      <div
        style={{
          position: 'absolute',
          width: '20px',
          height: '20px',
          background: `radial-gradient(circle, 
            var(--color-primary, #dc2626) 0%, 
            var(--color-accent, #8b5cf6) 100%
          )`,
          borderRadius: '50%',
          boxShadow: `
            0 0 15px rgba(var(--color-primary-rgb, 220, 38, 38), 0.5),
            inset 0 2px 4px rgba(255, 255, 255, 0.3)
          `,
          zIndex: 17,
        }}
      />
    </div>
  );
};

export default ClockHands;